import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';

import {
  Alert,
  Box,
  Button,
  Field,
  Flex,
  Input,
  PasswordInput,
  Text,
} from '@valwin/facto/components';

import { useErrorTranslator } from '~shared/i18n';

import {
  type LoginCipFormData,
  LoginCipFormErrors,
  loginCipSchema,
} from '../model/login-cip.schema';
import { usePostLoginCip } from '../model/usePostLoginCip';

export function LoginCipForm() {
  const { t } = useTranslation();

  const { loginCip, reset, isError, isPending } = usePostLoginCip();

  const translateError = useErrorTranslator<LoginCipFormErrors>({
    CIP_REQUIRED: () => t('auth cipRequired'),
    IDENTIFIER_REQUIRED: () => t('auth identifierRequired'),
    PASSWORD_REQUIRED: () => t('auth passwordRequired'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginCipFormData>({
    resolver: zodResolver(loginCipSchema),
  });

  const onSubmit = handleSubmit((data) => {
    loginCip(data);
  });

  const handleInputChange = () => {
    if (isError) {
      reset();
    }
  };

  return (
    <Flex flexDirection="column" gap="8" width="full">
      <Text textStyle="sm" color="gray.500" textAlign="center" marginBottom={0}>
        {t('auth loginCipInstructions')}
      </Text>
      <form onSubmit={onSubmit} noValidate>
        <Box display="flex" flexDirection="column" gap={5}>
          <Field.Root required invalid={!!errors.cip?.message}>
            <Field.Label>
              {t('auth cip')} <Field.RequiredIndicator />
            </Field.Label>
            <Input
              placeholder={t('auth cipPlaceholder')}
              {...register('cip', {
                onChange: handleInputChange,
              })}
            />
            <Field.ErrorText>
              {errors.cip?.message && translateError(errors.cip.message)}
            </Field.ErrorText>
          </Field.Root>

          <Field.Root required invalid={!!errors.email?.message}>
            <Field.Label>
              {t('auth identifier')} <Field.RequiredIndicator />
            </Field.Label>
            <Input
              placeholder={t('auth identifierPlaceholder')}
              {...register('email', {
                onChange: handleInputChange,
              })}
            />
            <Field.ErrorText>
              {errors.email?.message && translateError(errors.email.message)}
            </Field.ErrorText>
          </Field.Root>

          <Field.Root required invalid={!!errors.password?.message}>
            <Field.Label>
              {t('auth password')} <Field.RequiredIndicator />
            </Field.Label>
            <PasswordInput
              placeholder={t('auth passwordPlaceholder')}
              {...register('password', {
                onChange: handleInputChange,
              })}
            />
            <Field.ErrorText>
              {errors.password?.message &&
                translateError(errors.password.message)}
            </Field.ErrorText>
          </Field.Root>

          {isError && (
            <Alert.Root status="error">
              <Alert.Indicator />
              <Alert.Title>{t('auth invalidCipCredentials')}</Alert.Title>
            </Alert.Root>
          )}

          <Flex direction="row" justifyContent="flex-end" marginTop={4}>
            <Button
              type="submit"
              size="sm"
              disabled={
                isSubmitting || Object.values(errors).length > 0 || isPending
              }
              flex={{ base: '1 0 100%', md: '1' }}
            >
              {t('auth connect')}
            </Button>
          </Flex>
        </Box>
      </form>
    </Flex>
  );
}
